import { useState, type KeyboardEvent } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckIcon from '@mui/icons-material/Check';
import MenuIcon from '@mui/icons-material/Menu';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../core/contexts/AuthContext';
import { useThemeMode } from '../../../core/contexts/ThemeModeContext';
import { config } from '../../../core/config/env';
import { BrandMark } from '../BrandMark';
import { LiveDot } from '../LiveDot';
import { SIDEBAR_WIDTH } from './AppSidebar';
import { useAIChatContext } from '../../../ai-assistant/context/AIChatContext';
import { useProjectSelection } from '../../hooks/useProjectSelection';
import { SharedProjectChip } from '../SharedProjectChip';
import { useActiveProject } from '../../../core/contexts/ProjectContext';
import { ProjectTokenUsage } from '../../../modules/projects/ProjectTokenUsage';
import type { AccessibleProject } from '../../../core/types/data-models';

// ── Active project display + switcher (shown in header center) ────────────────

function ProjectDisplay() {
  const { own, shared, isLoading, switching, select, activeProject } = useProjectSelection();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const handleSelect = async (p: AccessibleProject) => {
    setAnchor(null);
    const next = await select(p);
    navigate(`/projects/${next.id}`);
  };

  const renderRow = (p: AccessibleProject) => (
    <MenuItem
      key={`${p.tenantId}-${p.id}`}
      selected={p.id === activeProject?.id}
      onClick={() => void handleSelect(p)}
      className={p.access === 'shared' ? 'shared-project-row' : undefined}
    >
      <Typography
        variant="body2"
        fontWeight={p.id === activeProject?.id ? 600 : 400}
        sx={{ flex: 1 }}
        noWrap
      >
        {p.name}
      </Typography>
      {p.access === 'shared' && (
        <SharedProjectChip tenantName={p.tenantName} role={p.role} sharedBy={p.sharedBy} />
      )}
      {p.id === activeProject?.id && <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />}
    </MenuItem>
  );

  return (
    <>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            px: 2,
            py: 0.75,
            cursor: switching ? 'wait' : 'pointer',
            minWidth: 200,
            userSelect: 'none',
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Typography
            variant="overline"
            sx={{ fontSize: 9, color: 'text.secondary', lineHeight: 1, display: 'block', letterSpacing: '0.08em' }}
          >
            Active project
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {activeProject && <LiveDot size={6} />}
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              color={activeProject ? 'text.primary' : 'text.disabled'}
            >
              {activeProject?.name ?? 'Select project…'}
            </Typography>
          </Stack>
        </Box>

        {/*
          Persistent marker while a shared project is active. This is the most
          important of the shared-project surfaces: it is on screen the entire
          time the user is working, so editing a client's document can never be
          mistaken for editing your own.
        */}
        {activeProject?.access === 'shared' && activeProject.tenantName && activeProject.role && (
          <SharedProjectChip
            tenantName={activeProject.tenantName}
            role={activeProject.role}
            sharedBy={activeProject.sharedBy}
            size="medium"
          />
        )}
      </Stack>

      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { minWidth: 320, maxHeight: 460, mt: 0.5 } } }}
      >
        {isLoading && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">Loading projects…</Typography>
          </MenuItem>
        )}
        {!isLoading && own.length === 0 && shared.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">No projects yet</Typography>
          </MenuItem>
        )}

        {own.length > 0 && (
          <Typography component="span" className="project-group-label">My workspace</Typography>
        )}
        {own.map(renderRow)}

        {shared.length > 0 && [
          <Divider key="shared-divider" sx={{ my: 0.5 }} />,
          <Typography component="span" className="project-group-label" key="shared-label">
            Shared with me
          </Typography>,
          ...shared.map(renderRow),
        ]}
      </Menu>
    </>
  );
}

// ── Header ─────────────────────────────────────────────────────────────────────

interface AppHeaderProps {
  /** Opens the overlay sidebar on small viewports; hamburger is hidden without it. */
  onMenuClick?: () => void;
  /**
   * `onboarding`: a project is being created — no search, the AI Assistant
   * is disabled, and the project switcher shows no selection (AppLayout
   * clears it).
   */
  variant?: 'default' | 'onboarding';
}

export function AppHeader({ onMenuClick, variant = 'default' }: AppHeaderProps) {
  const isOnboarding = variant === 'onboarding';
  const { user, logout } = useAuth();
  const { isOpen: isAssistantOpen, toggle: toggleAssistant } = useAIChatContext();
  const { mode, toggle: toggleThemeMode } = useThemeMode();
  const { activeProject } = useActiveProject();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const initial = user?.name?.[0] ?? user?.email?.[0] ?? '?';

  const submitSearch = () => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setSearchQuery('');
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitSearch();
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        backgroundColor: 'var(--color-bg-2)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ pl: 3 }}>
        {onMenuClick && (
          <IconButton
            onClick={onMenuClick}
            size="small"
            sx={{ display: { xs: 'inline-flex', lg: 'none' }, mr: 1.5 }}
            aria-label="Open navigation"
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        )}
        {/* Logo — click navigates to projects list */}
        <Stack
          component={RouterLink}
          to="/"
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            width: SIDEBAR_WIDTH - 24,
            mr: 3,
            flexShrink: 0,
            textDecoration: 'none',
            transition: 'opacity 0.15s',
            '&:hover': { opacity: 0.75 },
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
              boxShadow: 'var(--shadow-glow-primary)',
            }}
          >
            <BrandMark size={18} stroke="#ffffff" fill="#ffffff" />
          </Box>
          <Box>
            <Typography variant="h6" color="primary" lineHeight={1.1}>
              {config.appName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {config.appSubtitle}
            </Typography>
          </Box>
        </Stack>

        {/* Active project display + switcher (left-aligned, right after logo) */}
        <ProjectDisplay />

        <Box sx={{ flexGrow: 1 }} />

        {/* Content search — nothing to search while a project is being created. */}
        {!isOnboarding && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' }, mr: 2 }}>
            <TextField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search…"
              size="small"
              sx={{
                width: 260,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--color-bg)',
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Tooltip title="Search">
              <IconButton size="small" aria-label="Search" onClick={submitSearch}>
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {/* User info + logout */}
        <Stack direction="row" spacing={2} alignItems="center">
          {activeProject && (
            <>
              <Box
                sx={{
                  display: { xs: 'none', xl: 'flex' },
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 'var(--radius-full)',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'var(--color-bg)',
                }}
              >
                <ProjectTokenUsage projectId={activeProject.id} />
              </Box>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', xl: 'block' }, my: 1 }} />
            </>
          )}
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" fontWeight={600}>
              {user?.name ?? user?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.role}
            </Typography>
          </Box>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            {initial.toUpperCase()}
          </Avatar>
          <Tooltip title={mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
            <IconButton onClick={toggleThemeMode} size="small">
              {mode === 'dark' ? (
                <LightModeOutlinedIcon fontSize="small" sx={{ color: 'var(--color-warning)' }} />
              ) : (
                <DarkModeOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              isOnboarding
                ? 'The AI Assistant is available once the project exists'
                : isAssistantOpen
                  ? 'Close AI Assistant'
                  : 'Open AI Assistant'
            }
          >
            {/* span: a disabled button does not receive the hover events the tooltip needs. */}
            <span>
              <IconButton
                onClick={toggleAssistant}
                size="small"
                disabled={isOnboarding}
                aria-label="AI Assistant"
                sx={{
                  border: '1px solid',
                  borderRadius: 'var(--radius-md)',
                  borderColor: isAssistantOpen ? 'var(--color-primary-line)' : 'divider',
                  backgroundColor: isAssistantOpen ? 'var(--color-primary-soft)' : 'var(--color-bg)',
                  color: isAssistantOpen ? 'var(--color-primary)' : 'text.secondary',
                  transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
                  '&:hover': {
                    borderColor: 'var(--color-primary-line)',
                    backgroundColor: 'var(--color-primary-soft)',
                    color: 'var(--color-primary)',
                  },
                }}
              >
                <SmartToyOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Sign out">
            <IconButton onClick={logout} size="small">
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
